import { inject, Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, UrlTree } from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { concatMap, first } from 'rxjs/operators';

interface GuardConstructor {
  new(...args: any[]): CanActivate;
}

/**
 * Helper guard for running multiple guards in a synchronized fashion,
 * where the first that prevents activation (canActivate => false),
 * or returns a new route explicitly, will end the chain.
 *
 * This behavior diverges from Angular's default, where async guards will be run in parallel
 * and the result is determined once all returned promises & observables have resolved.
 */
@Injectable({
  providedIn: 'root'
})
export class SyncHelperGuard implements CanActivate {
  injector = inject(Injector);

  /**
   * override this member to provide an array of guards which should be run in succession
   * @protected
   */
  protected syncGuards?: GuardConstructor[] = null;

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return from(this.syncGuards || route.data.syncGuards)
      .pipe(
        // * make sure the guards await each other
        concatMap(value => {
          // * get the guard instance from the DI container
          const guard = this.injector.get(value);

          // ! duck-check whether we actually have a Guard instance
          if (!guard.canActivate) {
            throw new TypeError(`syncGuards value '${guard}' is not a Guard constructor`);
          }

          // * call the actual guard implementation
          const result = guard.canActivate(route, state);

          // * cast all possible return values to an Observable
          if (result instanceof Observable) {
            return result;
          } else if (result instanceof Promise) {
            return from(result);
          } // implicit else

          // * make sure the Guard returned a boolean
          // * (it will if it implements CanActivate correctly)
          if (typeof result !== 'boolean' && !(result instanceof UrlTree)) {
            throw new TypeError(`Guard returned '${result}' which seems to be incompatible with Angular's CanActivate spec.`);
          }

          // * continue or end the chain (true/false respectively)
          return of(result);
        }),
        first(x => x === false || x instanceof UrlTree, true)
      );
  }
}
